import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { setLinkLightAdminContext } from "../services/compliance-api";

const STORAGE_KEYS = {
  licenseKey: "linklight_root_admin_license_key",
  tenantId: "linklight_root_admin_tenant_id",
} as const;

interface LinkLightAdminSession {
  licenseKey: string;
  tenantId: string;
}

interface LinkLightAdminAuthValue {
  session: LinkLightAdminSession | null;
  isAuthenticated: boolean;
  login: (session: LinkLightAdminSession) => void;
  logout: () => void;
}

const LinkLightAdminAuthContext =
  createContext<LinkLightAdminAuthValue | null>(null);

function loadFromStorage(): LinkLightAdminSession | null {
  const licenseKey = localStorage.getItem(STORAGE_KEYS.licenseKey);
  const tenantId = localStorage.getItem(STORAGE_KEYS.tenantId);
  if (!licenseKey || !tenantId) return null;
  setLinkLightAdminContext(tenantId, licenseKey);
  return { licenseKey, tenantId };
}

export function LinkLightAdminAuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [session, setSession] = useState<LinkLightAdminSession | null>(
    loadFromStorage,
  );

  function login(nextSession: LinkLightAdminSession) {
    localStorage.setItem(STORAGE_KEYS.licenseKey, nextSession.licenseKey);
    localStorage.setItem(STORAGE_KEYS.tenantId, nextSession.tenantId);
    setSession(nextSession);
  }

  function logout() {
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    setSession(null);
  }

  return (
    <LinkLightAdminAuthContext.Provider
      value={{ session, isAuthenticated: session !== null, login, logout }}
    >
      {children}
    </LinkLightAdminAuthContext.Provider>
  );
}

export function useLinkLightAdminAuth(): LinkLightAdminAuthValue {
  const ctx = useContext(LinkLightAdminAuthContext);
  if (!ctx) {
    throw new Error(
      "useLinkLightAdminAuth must be used inside LinkLightAdminAuthProvider",
    );
  }
  return ctx;
}
